import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { supabase } from '../supabaseClient';
import UrlCustomization from '../components/UrlCustomization';

const Settings = ({ tenant }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [admins, setAdmins] = useState([]);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [adminForm, setAdminForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'admin',
    status: 'active'
  });

  useEffect(() => {
    if (activeTab === 'admins') {
      fetchAdmins();
    }
  }, [activeTab]);

  const fetchAdmins = async () => {
    try {
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setAdmins(data || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAdmin) {
        const { error } = await supabase
          .from('accounts')
          .update(adminForm)
          .eq('id', editingAdmin.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('accounts')
          .insert([{ ...adminForm, tenant_id: 'demo-tenant-id' }]);
        if (error) throw error;
      }
      
      setShowAdminForm(false);
      setEditingAdmin(null);
      setAdminForm({ first_name: '', last_name: '', email: '', phone: '', role: 'admin', status: 'active' });
      fetchAdmins();
    } catch (error) {
      console.error('Error saving admin:', error);
    }
  };

  const handleEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setAdminForm(admin);
    setShowAdminForm(true);
  };

  const handleDeleteAdmin = async (id) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      try {
        const { error } = await supabase
          .from('accounts')
          .delete()
          .eq('id', id);
        if (error) throw error;
        fetchAdmins();
      } catch (error) {
        console.error('Error deleting admin:', error);
      }
    }
  };

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <h2>Settings</h2>
        
        <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
          <button 
            onClick={() => setActiveTab('general')}
            className={`btn ${activeTab === 'general' ? 'btn-primary' : 'btn-secondary'}`}
          >
            General
          </button>
          <button 
            onClick={() => setActiveTab('admins')}
            className={`btn ${activeTab === 'admins' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Admins
          </button>
          <button 
            onClick={() => setActiveTab('urls')}
            className={`btn ${activeTab === 'urls' ? 'btn-primary' : 'btn-secondary'}`}
          >
            URLs
          </button>
          <button 
            onClick={() => setActiveTab('billing')}
            className={`btn ${activeTab === 'billing' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Billing
          </button>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ color: '#666', fontSize: '14px' }}>System and billing settings. For profile, notifications, and security settings, click your avatar in the top-right corner.</p>
        </div>

        {activeTab === 'general' && (
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
            <h3>General Settings</h3>
            <div style={{ display: 'grid', gap: '20px', maxWidth: '500px' }}>
              <div className="form-group">
                <label>Company Name</label>
                <input type="text" placeholder="Enter company name" defaultValue="Demo Property Management" />
              </div>
              <div className="form-group">
                <label>Time Zone</label>
                <select>
                  <option>Eastern Time (ET)</option>
                  <option>Central Time (CT)</option>
                  <option>Mountain Time (MT)</option>
                  <option>Pacific Time (PT)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Date Format</label>
                <select>
                  <option>MM/DD/YYYY</option>
                  <option>DD/MM/YYYY</option>
                  <option>YYYY-MM-DD</option>
                </select>
              </div>
              <div className="form-group">
                <label>Currency</label>
                <select>
                  <option>USD ($)</option>
                  <option>CAD (C$)</option>
                  <option>EUR (€)</option>
                </select>
              </div>
              <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '4px' }}>
                <h4>System Preferences</h4>
                <div style={{ display: 'grid', gap: '10px', marginTop: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="checkbox" defaultChecked />
                    Auto-save form data
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="checkbox" defaultChecked />
                    Show property photos in listings
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="checkbox" />
                    Enable dark mode
                  </label>
                </div>
              </div>
              <button className="btn btn-primary">Save Settings</button>
            </div>
          </div>
        )}

        {activeTab === 'admins' && (
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Admin Management</h3>
              <button 
                className="btn btn-primary"
                onClick={() => setShowAdminForm(true)}
              >
                Add Admin
              </button>
            </div>

            {showAdminForm && (
              <div style={{ marginBottom: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                <h4>{editingAdmin ? 'Edit Admin' : 'Add New Admin'}</h4>
                <form onSubmit={handleAdminSubmit} style={{ display: 'grid', gap: '15px', maxWidth: '500px' }}>
                  <div className="form-group">
                    <label>First Name</label>
                    <input 
                      type="text" 
                      value={adminForm.first_name}
                      onChange={(e) => setAdminForm({...adminForm, first_name: e.target.value})}
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input 
                      type="text" 
                      value={adminForm.last_name}
                      onChange={(e) => setAdminForm({...adminForm, last_name: e.target.value})}
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input 
                      type="email" 
                      value={adminForm.email}
                      onChange={(e) => setAdminForm({...adminForm, email: e.target.value})}
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input 
                      type="tel" 
                      value={adminForm.phone}
                      onChange={(e) => setAdminForm({...adminForm, phone: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select 
                      value={adminForm.role}
                      onChange={(e) => setAdminForm({...adminForm, role: e.target.value})}
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="staff">Staff</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select 
                      value={adminForm.status}
                      onChange={(e) => setAdminForm({...adminForm, status: e.target.value})}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button type="submit" className="btn btn-primary">
                      {editingAdmin ? 'Update' : 'Create'} Admin
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAdminForm(false);
                        setEditingAdmin(null);
                        setAdminForm({ first_name: '', last_name: '', email: '', phone: '', role: 'admin', status: 'active' });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div style={{ display: 'grid', gap: '10px' }}>
              {admins.map(admin => (
                <div key={admin.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '15px', 
                  border: '1px solid #eee', 
                  borderRadius: '8px' 
                }}>
                  <div>
                    <h4 style={{ margin: 0 }}>{admin.first_name} {admin.last_name}</h4>
                    <p style={{ margin: '5px 0', color: '#666' }}>{admin.email}</p>
                    <div style={{ display: 'flex', gap: '10px', fontSize: '12px' }}>
                      <span style={{ 
                        padding: '2px 8px', 
                        background: admin.role === 'admin' ? '#007bff' : '#6c757d', 
                        color: 'white', 
                        borderRadius: '12px' 
                      }}>
                        {admin.role}
                      </span>
                      <span style={{ 
                        padding: '2px 8px', 
                        background: admin.status === 'active' ? '#28a745' : '#dc3545', 
                        color: 'white', 
                        borderRadius: '12px' 
                      }}>
                        {admin.status}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => handleEditAdmin(admin)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => handleDeleteAdmin(admin.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {admins.length === 0 && (
                <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>No admins found. Add your first admin above.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'urls' && (
          <UrlCustomization tenantId={tenant?.id} />
        )}

        {activeTab === 'billing' && (
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
            <h3>Billing & Subscription</h3>
            <div style={{ display: 'grid', gap: '20px' }}>
              <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '4px' }}>
                <h4>Current Plan: Standard</h4>
                <p style={{ color: '#666' }}>$99/month - Up to 50 units</p>
                <button className="btn btn-primary">Upgrade Plan</button>
              </div>
              
              <div>
                <h4>Payment Method</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                  <span>Card</span>
                  <div>
                    <p style={{ margin: 0 }}>**** **** **** 1234</p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Expires 12/25</p>
                  </div>
                  <button className="btn btn-secondary" style={{ marginLeft: 'auto' }}>Update</button>
                </div>
              </div>
              
              <div>
                <h4>Billing History</h4>
                <div style={{ display: 'grid', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', border: '1px solid #eee', borderRadius: '4px' }}>
                    <span>December 2024</span>
                    <span>$99.00</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', border: '1px solid #eee', borderRadius: '4px' }}>
                    <span>November 2024</span>
                    <span>$99.00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Settings;